import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Download, Lock } from "lucide-react";
import { API, fmtBytes, fmtDate } from "@/lib/api";
import { Logo } from "@/pages/AuthLayout";
import { PreviewBody, safePreviewBlob } from "@/components/files/FilePreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function PublicLinkPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [url, setUrl] = useState(null);
  const [text, setText] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [noPreview, setNoPreview] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/links/${token}`).then((r) => { setInfo(r.data); if (!r.data.has_password) open(""); })
      .catch((e) => setError(e?.response?.data?.detail || "Lien invalide"));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBlob = (pw, download) => axios.post(`${API}/public/links/${token}/content`, { password: pw }, { params: { download }, responseType: "blob" });

  const open = async (pw) => {
    try {
      const r = await fetchBlob(pw, false);
      setError(""); setUnlocked(true);
      const type = (r.headers?.["content-type"] || "").split(";")[0].trim();
      if (type === "text/plain") setText(await r.data.text());
      else {
        try { setUrl(URL.createObjectURL(safePreviewBlob(r))); } catch { setNoPreview(true); }
      }
    } catch (e) { setError(e?.response?.status === 401 ? "Mot de passe incorrect" : e?.response?.status === 429 ? "Trop d'essais : réessayez dans 15 minutes" : "Accès impossible"); }
  };
  const download = async () => {
    const r = await fetchBlob(password, true);
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = info.file_name; a.click();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-link-page">
      <header className="h-16 bg-white border-b flex items-center px-6"><Logo /></header>
      <main className="max-w-4xl mx-auto p-4 sm:p-8">
        {error && !info && <div className="bg-white border rounded-xl p-10 text-center text-slate-600" data-testid="public-link-error">{error}</div>}
        {info && (
          <div className="bg-white border rounded-xl p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <div><h1 className="font-display text-xl font-bold" data-testid="public-file-name">{info.file_name}</h1>
                <p className="text-xs text-slate-500">{fmtBytes(info.size)} · partagé par {info.shared_by} · disponible jusqu'au {fmtDate(info.expires_at, true)}</p></div>
              {unlocked && info.allow_download && <Button onClick={download} data-testid="public-download-btn"><Download className="h-4 w-4 mr-2" />Télécharger</Button>}
            </div>
            {!unlocked ? (
              <form onSubmit={(e) => { e.preventDefault(); open(password); }} className="max-w-sm mx-auto py-10 text-center space-y-3">
                <Lock className="h-8 w-8 mx-auto text-[#1E3A8A]" />
                <p className="text-sm text-slate-600">Ce document est protégé par un mot de passe.</p>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe" data-testid="public-password-input" />
                {error && <p className="text-sm text-red-600" data-testid="public-password-error">{error}</p>}
                <Button type="submit" className="w-full" data-testid="public-password-submit">Accéder au document</Button>
              </form>
            ) : (
              <PreviewBody file={{ name: info.file_name, ext: info.ext, content_type: info.content_type }} url={url} textPreview={text} error={noPreview ? new Error("preview-not-allowed") : null} onDownload={info.allow_download ? download : null} />
            )}
          </div>
        )}
      </main>
    </div>
  );
}
